import React from "react";
import { Link } from "react-router-dom";
import {
  Moon, ArrowRight, UserCircle2, ShieldCheck, Sparkles, Heart,
} from "lucide-react";
import StarField from "../components/StarField";
import { VALUES, STATS, FAQ } from "../mock/mockData";

const ICONS = { UserCircle2, ShieldCheck, Sparkles, Heart };

const AboutPage = () => {
  return (
    <div className="relative" data-testid="about-page">
      {/* Header */}
      <section className="relative py-28 md:py-40 overflow-hidden">
        <StarField count={60} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,165,116,0.12),transparent_60%)]" />
        <div className="container-lux relative z-10 max-w-4xl fade-up">
          <div className="divider-gold mb-6">About Moon and Back</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] mb-8">
            Care that travels <em className="text-gold not-italic">to the moon</em> and back for you.
          </h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
            Moon and Back Support was founded on a simple belief: that every person deserves support delivered with dignity, warmth, and deep respect for who they are.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-28" data-testid="about-story">
        <div className="container-lux grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="divider-gold mb-6">Our Story</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-6">
              Born from a <em className="text-gold not-italic">genuine desire</em> to do care differently.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-white/75 leading-relaxed">
            <p>
              We saw families navigating a system that too often felt impersonal, inflexible, and transactional. We saw support workers rushed off their feet. We saw participants feeling like numbers on a roster.
            </p>
            <p>
              So we built something different - a team that shows up, follows through, and actually listens. A team that believes the quiet moments matter as much as the big ones. A team that treats every participant like family.
            </p>
            <p>
              Today, we support participants across Perth and surrounding areas with person-centred services that flex around real life. Our promise is the same now as it was on day one: <em className="text-gold not-italic">we're here for you, every step of the way.</em>
            </p>

            <div className="grid grid-cols-2 gap-8 pt-10 mt-10 border-t border-white/10 max-w-md">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`about-stat-${s.label}`}>
                  <div className="font-serif text-3xl text-gold">{s.value}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/50 mt-2">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-28 md:py-36 bg-gradient-to-b from-transparent via-[#0A0A12] to-transparent" data-testid="about-values">
        <div className="container-lux">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="divider-gold mb-6">What Guides Us</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Four quiet promises we keep <em className="text-gold not-italic">every single day.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {VALUES.map((v, i) => {
              const Icon = ICONS[v.icon];
              return (
                <div key={v.title} className="lux-card rounded-3xl p-10" data-testid={`about-value-${i}`}>
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-serif text-2xl text-white mb-3">{v.title}</h3>
                      <p className="text-white/65 leading-relaxed">{v.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-28 md:py-36" data-testid="about-faq">
        <div className="container-lux max-w-4xl">
          <div className="text-center mb-16">
            <div className="divider-gold mb-6">Your Questions</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Answered <em className="text-gold not-italic">honestly.</em>
            </h2>
          </div>
          <div className="space-y-4">
            {FAQ.map((item, i) => (
              <details key={i} className="lux-card rounded-2xl p-7 group" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="font-serif text-xl text-white pr-6">{item.q}</h3>
                  <div className="w-8 h-8 rounded-full border border-gold/30 flex items-center justify-center flex-shrink-0 transition-transform group-open:rotate-45">
                    <span className="text-gold text-lg leading-none">+</span>
                  </div>
                </summary>
                <p className="mt-5 text-white/70 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28" data-testid="about-cta">
        <div className="container-lux">
          <div className="lux-card rounded-[2rem] p-12 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="font-serif text-3xl md:text-4xl text-white mb-2">Ready to get started?</h3>
              <p className="text-white/60">A 15-minute conversation is all it takes to find out.</p>
            </div>
            <div className="flex gap-4 flex-wrap">
              <Link to="/book" className="btn-gold inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold tracking-[0.12em] uppercase text-deep hover:bg-gold-light transition-all hover:scale-[1.03]" data-testid="about-book-btn">
                <Moon className="w-4 h-4" /> Book Consultation
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-8 py-4 text-sm font-semibold tracking-[0.12em] uppercase text-gold hover:bg-gold/10 transition-all" data-testid="about-contact-btn">
                Contact Us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
